import React, { useEffect } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, Outlet, RouterProvider, useLocation, useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { MotionConfig } from 'framer-motion'
import './index.css'

import Home from './routes/Home'
import Communities from './routes/Communities'
import CommunityCosmos from './routes/CommunityCosmos'
import CommunityBase from './routes/CommunityBase'
import About from './routes/About'
import Contact from './routes/Contact'
import LifeCrafterProgram from './routes/LifeCrafterProgram'
import { Login, SignUp } from './routes/Auth'
import Legal from './routes/Legal'
import { communityPages, legalPages } from './data/content'
import { hydrateAuth } from './lib/auth'
import { DiagnosticBoundary } from './components/DiagnosticBoundary'

/* Browser back/forward restores the previous scroll position, which lands
   returning visitors mid-page and silently skips the scroll-driven hero beats
   (e.g. the home LifeCrafters beat). Every page owns its scroll from the top,
   so claim restoration before the router or Lenis initialise. */
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

/* every page opens at its top; browsers otherwise keep the old scroll */
function Root() {
  const { pathname } = useLocation()
  // NB: block body (not a concise arrow) so the effect always returns undefined.
  // A concise `() => window.scrollTo(0,0)` returns scrollTo's result as the effect
  // cleanup; browser extensions that monkeypatch window.scrollTo can make it return
  // a non-function, which React then tries to call on the next navigation and throws
  // "n is not a function" (the intermittent, extension-specific crash we chased).
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  // Browser back/forward restores the old scroll position AFTER the effect
  // above runs, so returning to a page lands mid-scroll and the pinned hero
  // beats (e.g. the home LifeCrafters beat) are skipped entirely. Scroll-
  // driven pages need every visit to start at the top, so own it ourselves.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual'
  }, [])
  // Restore the login session once, from the httpOnly cookie, on app start.
  useEffect(() => { void hydrateAuth() }, [])
  return (
    <DiagnosticBoundary>
      <Outlet />
    </DiagnosticBoundary>
  )
}

/* Graceful catch-all: instead of React Router's raw "UNEXPECTED APPLICATION
   ERROR" screen, show a friendly reload prompt so a runtime error (e.g. a
   third-party animation-lib effect misbehaving on an older browser) degrades
   into a recoverable state rather than a dead end. The real error is still
   logged to the console for debugging. */
function ErrorScreen() {
  const err = useRouteError()
  console.error('App error boundary caught:', err)
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0b0f0a', color: '#e9efe6', padding: 24, fontFamily: 'system-ui, sans-serif', textAlign: 'center' }}>
      <div style={{ maxWidth: 440 }}>
        <h1 style={{ fontSize: '1.6rem', marginBottom: 12 }}>Something went wrong</h1>
        <p style={{ opacity: 0.7, marginBottom: 24, fontSize: '0.95rem', lineHeight: 1.5 }}>
          The page hit an unexpected error. Reloading usually clears it.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{ background: '#c8f169', color: '#0b0f0a', border: 0, borderRadius: 999, padding: '12px 30px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}
        >
          Reload
        </button>
        <p style={{ marginTop: 18, fontSize: '0.9rem' }}>
          <a href="/" style={{ color: '#c8f169' }}>Back to home</a>
        </p>
        {isRouteErrorResponse(err) && <p style={{ marginTop: 12, opacity: 0.4, fontSize: '0.8rem' }}>{err.status} {err.statusText}</p>}
      </div>
    </div>
  )
}

const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <ErrorScreen />,
    children: [
  { path: '/', element: <Home /> },
  { path: '/login', element: <Login /> },
  { path: '/signup', element: <SignUp /> },
  { path: '/about', element: <About /> },
  { path: '/contact', element: <Contact /> },
  { path: '/lifecrafter-program', element: <LifeCrafterProgram /> },
  { path: '/communities', element: <Communities /> },
  { path: '/communities/cosmos', element: <CommunityCosmos dark /> },
  { path: '/communities/spirituality', element: <CommunityBase page={communityPages.spirituality} /> },
  { path: '/communities/zen', element: <CommunityBase page={communityPages.zen} /> },
  { path: '/communities/rhythm', element: <CommunityBase page={communityPages.rhythm} /> },
  { path: '/privacy-policy', element: <Legal page={legalPages.privacy} /> },
  { path: '/terms', element: <Legal page={legalPages.terms} /> },
  { path: '/instructor-policy', element: <Legal page={legalPages.instructor} /> },
    ],
  },
])

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <MotionConfig reducedMotion="user">
      <RouterProvider router={router} />
    </MotionConfig>
  </React.StrictMode>,
)
